import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Zap, Calendar, MessageSquare, Shield, Star, Droplets, Wrench,
  AlertTriangle, Home, Flame, Sparkles, Gauge, PipetteIcon
} from "lucide-react";

const AIReceptionistPlumberPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Plumbers Dallas | Plumbing Virtual Receptionist | 24/7 Plumber Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Emergency Dispatch", icon: Clock },
    { value: "After-Hours", label: "Emergency Coverage", icon: PhoneCall },
    { value: "<30s", label: "Response Time", icon: Zap },
    { value: "100%", label: "Calls Answered", icon: CheckCircle }
  ];

  const plumbingServices = [
    { icon: Droplets, name: "Water Leaks", desc: "Emergency leak calls, burst pipes, water damage prevention" },
    { icon: AlertTriangle, name: "Drain Emergencies", desc: "Clogged drains, sewage backups, main line issues" },
    { icon: Flame, name: "Water Heater", desc: "No hot water, repairs, replacement quotes, tankless inquiries" },
    { icon: Wrench, name: "General Repairs", desc: "Faucets, toilets, garbage disposals, routine fixes" },
    { icon: Home, name: "Remodeling", desc: "Bathroom/kitchen remodel quotes, fixture installation" },
    { icon: Gauge, name: "Inspections", desc: "Home inspections, sewer camera, leak detection" }
  ];

  const features = [
    { 
      icon: AlertTriangle, 
      title: "Emergency Dispatch", 
      desc: "Instantly routes burst pipes, flooding, and sewage emergencies. Gets your plumber rolling while competitors go to voicemail." 
    },
    { 
      icon: Calendar, 
      title: "Smart Scheduling", 
      desc: "Books service calls by zone, urgency, and job type. Integrates with ServiceTitan, Jobber, and Housecall Pro." 
    },
    { 
      icon: DollarSign, 
      title: "Quote Qualification", 
      desc: "Captures project details for water heater replacements, remodels, and big jobs. Your estimator gets qualified leads, not tire-kickers." 
    },
    { 
      icon: MessageSquare, 
      title: "Service Area Filter", 
      desc: "Checks if callers are in your service area. Politely refers out-of-area calls while capturing in-area leads." 
    },
    { 
      icon: Shield, 
      title: "After-Hours Triage", 
      desc: "Determines real emergencies vs. next-day calls. Saves on unnecessary after-hours dispatches, never misses true emergencies." 
    },
    { 
      icon: Sparkles, 
      title: "Maintenance Programs", 
      desc: "Promotes your drain cleaning or maintenance plans. Books preventive services during slow periods." 
    }
  ];

  const comparisons = [
    { item: "Dispatcher/Receptionist", traditional: "$35,000-$45,000/year", ai: "$199-$399/month" },
    { item: "After-Hours Answering", traditional: "$500-$1,200/month", ai: "Included 24/7" },
    { item: "Missed Emergency Calls", traditional: "Lost to competitors", ai: "0% - All answered" },
    { item: "Call Quality", traditional: "Varies by staff", ai: "Consistent excellence" },
    { item: "Spanish-Speaking", traditional: "Rare/Extra cost", ai: "Included" },
    { item: "CRM Integration", traditional: "Manual entry", ai: "Automatic sync" }
  ];

  const faqs = [
    { 
      q: "How does AI handle plumbing emergency calls?", 
      a: "When someone calls about a burst pipe, flooding, or sewage backup, our AI immediately recognizes the emergency. It captures the address, nature of the problem, and water shut-off status, then instantly alerts your on-call plumber via call, text, and app notification. Your plumber gets complete details before callback—average dispatch time is under 5 minutes for true emergencies."
    },
    { 
      q: "Can AI distinguish between emergency and routine calls?",
      a: "Yes. The AI asks targeted questions to assess urgency: Is water actively flowing? Can you shut off the water? Is there sewage involved? Based on answers, it either dispatches immediately for emergencies or schedules appropriately for routine repairs. This prevents unnecessary after-hours call-outs while ensuring real emergencies get immediate response."
    },
    { 
      q: "Does it integrate with plumbing dispatch software?",
      a: "We integrate with ServiceTitan, Jobber, Housecall Pro, FieldEdge, and other popular platforms. The AI creates jobs, assigns to available plumbers, and updates your schedule in real-time. No double-entry—everything syncs automatically."
    },
    { 
      q: "How does it qualify water heater replacement leads?",
      a: "For replacement or big-ticket inquiries, the AI gathers: current water heater type and age, household size, fuel type (gas/electric), tankless interest, timeline, and budget range. Your estimator receives qualified leads with all details needed for accurate quotes. Price shoppers get basic info without wasting your time."
    },
    { 
      q: "What about service area filtering?",
      a: "The AI knows your service zones. When callers are outside your area, it politely explains and can even refer to partner plumbers you designate. For callers in your zone, it captures the full address and job details. No more wasted trips to discover the job is 50 miles away."
    },
    { 
      q: "Can it help fill the schedule during slow seasons?",
      a: "Yes. The AI can proactively contact past customers for water heater maintenance, drain cleaning services, or seasonal promotions. It books preventive maintenance during slow periods and can even promote your maintenance plan to every caller, turning one-time repairs into recurring revenue."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Plumbers",
        "description": "24/7 AI-powered virtual receptionist for plumbing companies in Dallas-Fort Worth. Emergency dispatch, service scheduling, and lead qualification.",
        "provider": { "@type": "LocalBusiness", "name": "Digital Media Ninja" },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney"],
        "serviceType": "AI Virtual Receptionist for Plumbers"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Plumber Receptionist Service",
        "offers": { "@type": "AggregateOffer", "lowPrice": "199", "highPrice": "399", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-plumber-page">
      <Helmet>
        <title>AI Receptionist for Plumbers Dallas | 24/7 Plumbing Answering Service | Virtual Dispatcher DFW</title>
        <meta name="description" content="AI receptionist for Dallas plumbers. 24/7 emergency dispatch, service scheduling, lead qualification. ServiceTitan & Jobber integration. Never miss a burst pipe call. Free demo!" />
        <meta name="keywords" content="AI receptionist plumber Dallas, plumbing answering service, plumber virtual receptionist DFW, 24/7 plumber dispatch, plumbing phone answering, emergency plumber answering service" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-plumbers" />
        <meta property="og:title" content="AI Receptionist for Plumbers Dallas | 24/7 Emergency Dispatch" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-blue-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
                <Droplets className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">Built for Plumbing Contractors</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-blue-400">Plumbers</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Capture every emergency call. AI dispatches your plumbers 24/7, qualifies leads, and books service calls—even while you're fixing pipes.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="general"
                  size="lg"
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-blue-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Droplets className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Plumbing Dispatch AI</h3>
                      <p className="text-blue-400">24/7 Emergency Ready</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Instant emergency dispatch for burst pipes", "ServiceTitan & Jobber integration", "Smart service area filtering", "Lead qualification for big jobs"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Plumbing Services We Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plumbingServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-blue-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-blue-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Plumbing Contractors</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Plumbing Dispatch Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-blue-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-blue-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-blue-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600/20 to-blue-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Capture Every Plumbing Call?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas plumbers using AI to answer 100% of calls and dispatch instantly.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistPlumberPage;
